import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCcStripe } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SignaturePad from '../components/SignaturePad';
import SumsubKYC from '../components/kyc/SumsubKYC';
import { branding } from '../constants/branding';
import { registrationService } from '../services/registrationService';
import { kycService } from '../services/kycService';
import { ProductSelection } from '../components/common/ProductSelection';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { emailVerificationService } from '../services/emailVerificationService';
import './EnhancedRegistration.css';

const API_BASE_URL = getApiBaseUrl();

// Enhanced Business Form Data Type
type EnhancedBusinessFormData = {
  // Product Information
  productId: number;
  productType: string;
  registrationFee: number;
  registrationFeeType: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY';
  requiresVerification: boolean;

  // Company Information
  companyName: string;
  registrationNumber: string;
  dateOfIncorporation: string;
  countryOfIncorporation: string;
  businessType: string;
  businessTypeOther: string;
  industry: string;
  website: string;
  email: string;
  phone: string;
  
  // Registered Address
  registeredAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  
  // Business Address
  businessAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  
  // Directors/Shareholders
  directors: Array<{
    name: string;
    position: string;
    nationality: string;
    shareholding: string;
    address: string;
  }>;
  
  // Authorized Signatories
  signatories: Array<{
    name: string;
    position: string;
    authority: string;
    address: string;
  }>;
  
  // Business Activity
  businessActivity: string;
  expectedMonthlyTurnover: string;
  sourceOfFunds: string;
  purposeOfAccount: string;
  
  // Compliance
  underSanctions: boolean;
  sanctionType: string;
  sanctionFrom: string;
  pep: boolean;
  pepDetails: Array<{
    name: string;
    position: string;
    organization: string;
    country: string;
    untilWhen: string;
  }>;
  
  // Documents
  documents: {
    certificateOfIncorporation: boolean;
    memorandumOfAssociation: boolean;
    articlesOfAssociation: boolean;
    boardResolution: boolean;
    beneficialOwnership: boolean;
    financialStatements: boolean;
    businessPlan: boolean;
    other: boolean;
    otherSpecify: string;
  };
  
  documentComments: {
    certificateOfIncorporation: string;
    memorandumOfAssociation: string;
    articlesOfAssociation: string;
    boardResolution: string;
    beneficialOwnership: string;
    financialStatements: string;
    businessPlan: string;
    other: string;
  };
  
  uploadedDocuments: { [key: string]: File[] };
  sumsubVerificationCompleted: boolean;
  
  // Terms and Marketing
  terms: boolean;
  marketing: boolean;
  signatureDataUrl?: string | null;
};

const EnhancedBusinessRegister: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reviewRef = useRef<HTMLDivElement | null>(null);
  
  // KYC state variables
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycApplicantId, setKycApplicantId] = useState<string | null>(null);
  const [kycAccessToken, setKycAccessToken] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<'pending' | 'completed' | 'rejected' | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED'>('PENDING');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [emailVerificationMessage, setEmailVerificationMessage] = useState('');

  const steps = [
    'Product Selection',
    'Company Information',
    'Registered Address',
    'Business Address', 
    'Directors & Shareholders',
    'Authorized Signatories',
    'Business Activity',
    'Compliance',
    'Documents',
    'Review & Sign'
  ];

  const [formData, setFormData] = useState<EnhancedBusinessFormData>({
    // Product Information defaults
    productId: 0,
    productType: '',
    registrationFee: 0,
    registrationFeeType: 'FIXED_ONE_OFF',
    requiresVerification: true,

    // Company Information defaults
    companyName: '',
    registrationNumber: '',
    dateOfIncorporation: '',
    countryOfIncorporation: '',
    businessType: '',
    businessTypeOther: '',
    industry: '',
    website: '',
    email: '',
    phone: '',
    
    // Address defaults
    registeredAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    businessAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    
    // Directors/Shareholders defaults
    directors: [
      { name: '', position: '', nationality: '', shareholding: '', address: '' },
      { name: '', position: '', nationality: '', shareholding: '', address: '' }
    ],
    
    // Signatories defaults
    signatories: [
      { name: '', position: '', authority: '', address: '' },
      { name: '', position: '', authority: '', address: '' }
    ],
    
    // Business Activity defaults
    businessActivity: '',
    expectedMonthlyTurnover: '',
    sourceOfFunds: '',
    purposeOfAccount: '',
    
    // Compliance defaults
    underSanctions: false,
    sanctionType: '',
    sanctionFrom: '',
    pep: false,
    pepDetails: [
      { name: '', position: '', organization: '', country: '', untilWhen: '' },
      { name: '', position: '', organization: '', country: '', untilWhen: '' }
    ],
    
    // Document defaults
    documents: {
      certificateOfIncorporation: false,
      memorandumOfAssociation: false,
      articlesOfAssociation: false,
      boardResolution: false,
      beneficialOwnership: false,
      financialStatements: false,
      businessPlan: false,
      other: false,
      otherSpecify: ''
    },
    documentComments: {
      certificateOfIncorporation: '',
      memorandumOfAssociation: '',
      articlesOfAssociation: '',
      boardResolution: '',
      beneficialOwnership: '',
      financialStatements: '',
      businessPlan: '',
      other: ''
    },
    uploadedDocuments: {},
    sumsubVerificationCompleted: false,
    
    // Terms and Marketing defaults
    terms: false,
    marketing: false,
    signatureDataUrl: null
  });

  const verificationRequired = formData.requiresVerification !== false;

  useEffect(() => {
    initializeBrandingStyles();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment_result');
    const sessionId = params.get('session_id');
    if (!paymentResult) return;

    const finalize = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
      setActiveStep(steps.length - 1);
    };

    if (paymentResult === 'cancelled') {
      setPaymentStatus('EXPIRED');
      setPaymentCompleted(false);
      setError('Payment was cancelled. You can try again.');
      finalize();
      return;
    }

    if (paymentResult === 'success' && sessionId) {
      const checkStatus = async () => {
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
          const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
          setPaymentSessionId(sessionId);
          setPaymentStatus(status);
          setPaymentCompleted(status === 'COMPLETED');
          if (status !== 'COMPLETED') {
            setError('');
            startPaymentStatusPolling(sessionId);
          }
        } catch {
          setError('Unable to verify payment status right now. Please try again.');
        } finally {
          finalize();
        }
      };
      checkStatus();
    }
  }, []);

  useEffect(() => {
    setEmailVerified(false);
    setEmailVerificationSent(false);
    setEmailVerificationMessage('');
  }, [formData.email]);

  const sendVerificationLink = async () => {
    if (!formData.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      setEmailVerificationMessage('');
      const result = await emailVerificationService.sendLink(formData.email);
      setEmailVerificationSent(true);
      setEmailVerificationMessage(result.message || 'Verification link sent.');
    } catch (err: any) {
      setEmailVerificationMessage(
        err?.response?.data?.errors?.[0]
          || err?.response?.data?.message
          || 'Failed to send verification link.'
      );
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const refreshEmailVerificationStatus = async () => {
    if (!formData.email) {
      setEmailVerificationMessage('Enter an email first.');
      return;
    }
    try {
      setEmailVerificationLoading(true);
      const status = await emailVerificationService.getStatus(formData.email);
      setEmailVerified(Boolean(status.verified));
      setEmailVerificationMessage(status.verified
        ? 'Email verified. You can continue.'
        : 'Email not verified yet. Click the verification link and check again.'
      );
    } catch {
      setEmailVerificationMessage('Failed to check verification status.');
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const isStepValid = useMemo(() => {
    switch (activeStep) {
      case 0:
        return !!(formData.productId && formData.productId > 0);
      case 1:
        return !!(formData.companyName && formData.registrationNumber && formData.dateOfIncorporation && formData.countryOfIncorporation && formData.email && emailVerified);
      case 2:
        return !!(formData.registeredAddress.street && formData.registeredAddress.city && formData.registeredAddress.country);
      case 3:
        return !!(formData.businessAddress.street && formData.businessAddress.city && formData.businessAddress.country);
      case 4:
        return formData.directors.some(director => director.name && director.position);
      case 5:
        return formData.signatories.some(signatory => signatory.name && signatory.position);
      case 6:
        return !!(formData.businessActivity && formData.expectedMonthlyTurnover);
      case 7:
        return true; // Compliance step - no required fields
      case 8: {
        const hasDocuments = Object.values(formData.documents).some(selected => selected);
        return hasDocuments && (!verificationRequired || formData.sumsubVerificationCompleted);
      }
      case 9:
        return !!formData.signatureDataUrl;
      default:
        return false;
    }
  }, [activeStep, formData, verificationRequired]);

  const handleNext = async () => {
    if (!isStepValid) return;
    if (activeStep < steps.length - 1) {
      setActiveStep(s => s + 1);
      return;
    }
    if (formData.registrationFeeType !== 'NO_FEE' && !paymentCompleted) {
      setError('Please complete the registration payment before continuing.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // First generate and download PDF, and keep a copy to upload after submit
      const pdfFile = await handleDownloadPdf();
      
      // Then submit the registration data
      // Create FormData object
      const formDataObj = new FormData();
      
      // Add JSON data as a string
      const { uploadedDocuments, ...formDataWithoutFiles } = formData;
      const jsonData = JSON.stringify({
        ...formDataWithoutFiles,
        signatureDataUrl: formData.signatureDataUrl || undefined
      });
      console.log('Sending form data:', jsonData);
      formDataObj.append('formData', jsonData);

      // Log FormData contents
      console.log('FormData entries:');
      Array.from(formDataObj.entries()).forEach(([key, value]) => {
        console.log(key, ':', typeof value === 'string' ? value.substring(0, 100) + '...' : 'File');
      });

      // Add files if they exist
      Object.entries(formData.uploadedDocuments || {}).forEach(([key, files]) => {
        if (files?.[0]) {
          files.forEach((file, index) => {
            formDataObj.append(key, file);
          });
        }
      });

      const response = await registrationService.submitEnhancedBusinessRegistration(formDataObj);

      if (response.success) {
        // Upload the generated PDF after successful submission
        try {
          if (pdfFile) {
            await registrationService.uploadEnhancedBusinessSummaryPdf(formData.email, pdfFile);
          }
        } catch (uploadErr) {
          console.error('Failed to upload generated PDF after submission:', uploadErr);
        }
        // Navigate to login page after successful registration
        navigate('/login', {
          state: {
            message: 'Business registration submitted successfully! Our compliance team will review your application. Please login to continue.',
            type: 'success'
          }
        });
      } else {
        setError(response.error || 'Failed to submit registration');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationPayment = () => {
    setError('');
    if (!formData.productId || Number(formData.registrationFee || 0) <= 0) {
      setError('Please select a tier with a valid registration fee before starting payment.');
      return;
    }
    const startPayment = async () => {
      const response = await axios.post(`${API_BASE_URL}/api/public/registration-payments/create-session`, {
        registrationType: 'BUSINESS',
        email: formData.email,
        productId: formData.productId,
        currency: 'EUR'
      });
      if (!response.data?.success || !response.data?.data?.checkoutUrl || !response.data?.data?.sessionId) {
        setError(response.data?.error || 'Failed to create payment session.');
        return;
      }
      const sessionId = response.data.data.sessionId as string;
      setPaymentSessionId(sessionId);
      setPaymentStatus('PROCESSING');
      window.open(response.data.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      startPaymentStatusPolling(sessionId);
    };
    startPayment().catch((err: any) => setError(err?.response?.data?.error || err?.message || 'Failed to start payment.'));
  };

  const startPaymentStatusPolling = (sessionId: string) => {
    const maxAttempts = 60;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const statusRes = await axios.get(`${API_BASE_URL}/api/public/registration-payments/status/${sessionId}`);
        const status = (statusRes.data?.data?.status || 'PROCESSING') as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
        setPaymentStatus(status);
        if (status === 'COMPLETED') {
          setPaymentCompleted(true);
          clearInterval(interval);
        }
        if (status === 'FAILED' || status === 'EXPIRED') {
          setPaymentCompleted(false);
          clearInterval(interval);
        }
      } catch {
        // keep polling
      }
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 5000);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(s => s - 1);
    }
  };

  const handleChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayChange = (arrayName: string, index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: (prev[arrayName as keyof typeof prev] as any[]).map((item: any, i: number) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleDownloadPdf = async (): Promise<File | null> => {
    try {
      setLoading(true);
      setError('');

      const doc = new jsPDF('p', 'mm', 'a4');
      const margin = 15;
      let y = 20;

      const addH = (title: string) => {
        y += 8; // Add extra spacing before section title
        if (y > 270) { 
          doc.addPage(); 
          y = 20; 
        }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
        doc.text(title, margin, y); y += 8;
        doc.setDrawColor(0,0,0); doc.line(margin, y, 195, y); y += 6;
      };
      const addKV = (k: string, v: string) => {
        // Calculate dynamic key width based on key length
        const keyText = `${k}:`;
        const keyWidth = Math.max(50, doc.getTextWidth(keyText) + 5); // Minimum 50mm, or text width + 5mm
        const valueStartX = margin + keyWidth;
        const maxWidth = 195 - valueStartX; // Available width for values
        
        // Add key
        doc.setFont('helvetica', 'bold'); 
        doc.setFontSize(10); 
        doc.text(keyText, margin, y);
        
        // Add value with text wrapping
        doc.setFont('helvetica', 'normal'); 
        const value = v || '-';
        
        // Split long text into multiple lines
        const lines = doc.splitTextToSize(value, maxWidth);
        const lineHeight = 5;
        
        // Draw each line
        lines.forEach((line: string, index: number) => {
          if (y > 270) { 
            doc.addPage(); 
            y = 20; 
          }
          doc.text(line, valueStartX, y);
          if (index < lines.length - 1) {
            y += lineHeight;
          }
        });
        
        y += lineHeight + 1; // Add spacing after the field
        if (y > 270) { 
          doc.addPage(); 
          y = 20; 
        }
      };

      // Title
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
      doc.text('Enhanced Business Registration Form', margin, y); y += 10;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y); y += 10;

      // 1. Selected Product
      addH('1. Selected Product');
      addKV('Product ID', formData.productId.toString());
      addKV('Product Type', formData.productType);

      // 2. Company Information
      addH('2. Company Information');
      addKV('Company Name', formData.companyName);
      addKV('Registration Number', formData.registrationNumber);
      addKV('Date of Incorporation', formData.dateOfIncorporation);
      addKV('Country of Incorporation', formData.countryOfIncorporation);
      addKV('Business Type', formData.businessType + (formData.businessType === 'Other' && formData.businessTypeOther ? ` - ${formData.businessTypeOther}` : ''));
      addKV('Industry', formData.industry);
      addKV('Website', formData.website);
      addKV('Email', formData.email);
      addKV('Phone', formData.phone);

      // 3. Registered Address
      addH('3. Registered Address');
      addKV('Street', formData.registeredAddress.street);
      addKV('City', formData.registeredAddress.city);
      addKV('State', formData.registeredAddress.state);
      addKV('Postal Code', formData.registeredAddress.postalCode);
      addKV('Country', formData.registeredAddress.country);

      // 4. Business Address
      addH('4. Business Address');
      addKV('Street', formData.businessAddress.street);
      addKV('City', formData.businessAddress.city);
      addKV('State', formData.businessAddress.state);
      addKV('Postal Code', formData.businessAddress.postalCode);
      addKV('Country', formData.businessAddress.country);

      // 5. Directors & Shareholders
      addH('5. Directors & Shareholders');
      formData.directors.forEach((director, index) => {
        addKV(`Director/Shareholder ${index + 1} Name`, director.name);
        addKV(`Director/Shareholder ${index + 1} Position`, director.position);
        addKV(`Director/Shareholder ${index + 1} Nationality`, director.nationality);
        addKV(`Director/Shareholder ${index + 1} Shareholding`, director.shareholding);
        addKV(`Director/Shareholder ${index + 1} Address`, director.address);
        if (index < formData.directors.length - 1) {
          y += 3; // Add spacing between directors
        }
      });

      // 6. Authorized Signatories
      addH('6. Authorized Signatories');
      formData.signatories.forEach((signatory, index) => {
        addKV(`Signatory ${index + 1} Name`, signatory.name);
        addKV(`Signatory ${index + 1} Position`, signatory.position);
        addKV(`Signatory ${index + 1} Authority`, signatory.authority);
        addKV(`Signatory ${index + 1} Address`, signatory.address);
        if (index < formData.signatories.length - 1) {
          y += 3; // Add spacing between signatories
        }
      });

      // 7. Business Activity
      addH('7. Business Activity');
      addKV('Business Activity Description', formData.businessActivity);
      addKV('Expected Monthly Turnover', formData.expectedMonthlyTurnover);
      addKV('Source of Funds', formData.sourceOfFunds);
      addKV('Purpose of Account', formData.purposeOfAccount);

      // 8. Compliance
      addH('8. Compliance');
      addKV('Under Sanctions', formData.underSanctions ? 'Yes' : 'No');
      addKV('Politically Exposed Person', formData.pep ? 'Yes' : 'No');

      // 9. Documents
      addH('9. Documents');
      addKV('Certificate of Incorporation', formData.documents.certificateOfIncorporation ? 'Yes' : 'No');
      addKV('Memorandum of Association', formData.documents.memorandumOfAssociation ? 'Yes' : 'No');
      addKV('Articles of Association', formData.documents.articlesOfAssociation ? 'Yes' : 'No');
      addKV('Board Resolution', formData.documents.boardResolution ? 'Yes' : 'No');
      addKV('Beneficial Ownership', formData.documents.beneficialOwnership ? 'Yes' : 'No');
      addKV('Financial Statements', formData.documents.financialStatements ? 'Yes' : 'No');
      addKV('Business Plan', formData.documents.businessPlan ? 'Yes' : 'No');
      addKV('Other Documents', formData.documents.other ? `Yes - ${formData.documents.otherSpecify}` : 'No');
      if (formData.requiresVerification !== false) {
        addKV('SUMSUB Verification', formData.sumsubVerificationCompleted ? 'Completed' : 'Not Completed');
      } else {
        addKV('SUMSUB Verification', 'Not required for selected product');
      }

      // 10. Signature
      addH('10. Signature');
      if (formData.signatureDataUrl) {
        const img = formData.signatureDataUrl;
        doc.addImage(img, 'PNG', margin, y, 60, 24);
        y += 30;
      } else {
        addKV('Signature', 'Not provided');
      }

      const filename = `Enhanced_Business_Registration_${formData.companyName || 'Form'}.pdf`;
      const blob = doc.output('blob');
      const file = new File([blob], filename, { type: 'application/pdf' });
      doc.save(filename);
      return file;
    } catch (e: any) {
      setError('Failed to generate PDF');
      console.error('PDF generation error:', e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fillWithMockData = () => {
    setFormData(prevData => ({
      productId: 4, // Business Current Account
      productType: 'BANKING',
      registrationFee: 25,
      registrationFeeType: 'FIXED_ONE_OFF',
      requiresVerification: true,
      companyName: 'Tech Solutions Ltd',
      registrationNumber: 'REG123456789',
      dateOfIncorporation: '2020-01-15',
      countryOfIncorporation: 'United Kingdom',
      businessType: 'Private Limited Company',
      businessTypeOther: '',
      industry: 'Technology',
      website: 'https://techsolutions.com',
      email: 'info@techsolutions.com',
      phone: '+44-20-1234-5678',
      
      registeredAddress: {
        street: '123 Business Street',
        city: 'London',
        state: 'England',
        postalCode: 'SW1A 1AA',
        country: 'United Kingdom'
      },
      businessAddress: {
        street: '456 Tech Avenue',
        city: 'London',
        state: 'England',
        postalCode: 'EC1A 4HD',
        country: 'United Kingdom'
      },
      
      directors: [
        { name: 'John Smith', position: 'Managing Director', nationality: 'British', shareholding: '60%', address: '789 Director Lane, London, UK' },
        { name: 'Jane Doe', position: 'Finance Director', nationality: 'British', shareholding: '40%', address: '321 Executive Road, London, UK' },
        { name: 'Mike Johnson', position: 'Non-Executive Director', nationality: 'American', shareholding: '0%', address: '456 Business Ave, New York, USA' }
      ],
      
      signatories: [
        { name: 'John Smith', position: 'Managing Director', authority: 'Full signing authority', address: '789 Director Lane, London, UK' },
        { name: 'Jane Doe', position: 'Finance Director', authority: 'Financial transactions up to £10,000', address: '321 Executive Road, London, UK' },
        { name: 'Sarah Wilson', position: 'Operations Manager', authority: 'Operational transactions up to £5,000', address: '789 Operations Street, London, UK' }
      ],
      
      businessActivity: 'Software development and IT consulting services',
      expectedMonthlyTurnover: '£50,000',
      sourceOfFunds: 'Business operations and client payments',
      purposeOfAccount: 'Business banking and operations',
      
      underSanctions: false,
      sanctionType: '',
      sanctionFrom: '',
      pep: false,
      pepDetails: [
        { name: '', position: '', organization: '', country: '', untilWhen: '' },
        { name: '', position: '', organization: '', country: '', untilWhen: '' }
      ],
      
      documents: {
        certificateOfIncorporation: true,
        memorandumOfAssociation: true,
        articlesOfAssociation: true,
        boardResolution: true,
        beneficialOwnership: true,
        financialStatements: true,
        businessPlan: true,
        other: false,
        otherSpecify: ''
      },
      documentComments: {
        certificateOfIncorporation: 'Original certificate',
        memorandumOfAssociation: 'Latest version',
        articlesOfAssociation: 'Current articles',
        boardResolution: 'Banking authority resolution',
        beneficialOwnership: 'BO register',
        financialStatements: 'Latest audited accounts',
        businessPlan: '3-year business plan',
        other: ''
      },
      uploadedDocuments: {},
      sumsubVerificationCompleted: true,
      
      terms: true,
      marketing: false,
      signatureDataUrl: null
    } as EnhancedBusinessFormData));
  };

  const clearAllData = () => {
    setFormData(prevData => ({
      productId: 0,
      productType: '',
      registrationFee: 0,
      registrationFeeType: 'FIXED_ONE_OFF',
      requiresVerification: true,
      companyName: '',
      registrationNumber: '',
      dateOfIncorporation: '',
      countryOfIncorporation: '',
      businessType: '',
      businessTypeOther: '',
      industry: '',
      website: '',
      email: '',
      phone: '',
      
      registeredAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      businessAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      
      directors: [
        { name: '', position: '', nationality: '', shareholding: '', address: '' },
        { name: '', position: '', nationality: '', shareholding: '', address: '' }
      ],
      
      signatories: [
        { name: '', position: '', authority: '', address: '' },
        { name: '', position: '', authority: '', address: '' }
      ],
      
      businessActivity: '',
      expectedMonthlyTurnover: '',
      sourceOfFunds: '',
      purposeOfAccount: '',
      
      underSanctions: false,
      sanctionType: '',
      sanctionFrom: '',
      pep: false,
      pepDetails: [
        { name: '', position: '', organization: '', country: '', untilWhen: '' },
        { name: '', position: '', organization: '', country: '', untilWhen: '' }
      ],
      
      documents: {
        certificateOfIncorporation: false,
        memorandumOfAssociation: false,
        articlesOfAssociation: false,
        boardResolution: false,
        beneficialOwnership: false,
        financialStatements: false,
        businessPlan: false,
        other: false,
        otherSpecify: ''
      },
      documentComments: {
        certificateOfIncorporation: '',
        memorandumOfAssociation: '',
        articlesOfAssociation: '',
        boardResolution: '',
        beneficialOwnership: '',
        financialStatements: '',
        businessPlan: '',
        other: ''
      },
      uploadedDocuments: {},
      sumsubVerificationCompleted: false,
      
      terms: false,
      marketing: false,
      signatureDataUrl: null
    } as EnhancedBusinessFormData));
  };

  const renderStep = () => {
    const input = (label: string, field: string, type: string = 'text') => (
      <div className="form-group" style={{ marginBottom: '8px' }}>
        <label className="form-label" style={{ fontSize: '13px', marginBottom: '4px', color: '#374151' }}>{label}</label>
        <input
          type={type}
          className="form-input"
          style={{ 
            height: '32px',
            fontSize: '13px',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #e5e7eb'
          }}
          value={formData[field as keyof typeof formData] as string}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      </div>
    );

    const addressInput = (label: string, field: string, addressType: 'registeredAddress' | 'businessAddress') => (
      <div className="form-group" style={{ marginBottom: '8px' }}>
        <label className="form-label" style={{ fontSize: '13px', marginBottom: '4px', color: '#374151' }}>{label}</label>
        <input
          type="text"
          className="form-input"
          style={{ 
            height: '32px',
            fontSize: '13px',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #e5e7eb'
          }}
          value={formData[addressType][field as keyof typeof formData[typeof addressType]] as string}
          onChange={(e) => handleChange(`${addressType}.${field}`, e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      </div>
    );

    switch (activeStep) {
      case 0:
        return (
          <ProductSelection
            customerType="BUSINESS"
            onProductSelect={(productId: number, productType: string, registrationFee?: number, registrationFeeType?: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY', requiresVerification?: boolean) => {
              const needsVerification = requiresVerification !== false;
              const feeType = registrationFeeType || 'FIXED_ONE_OFF';
              setFormData(prev => ({
                ...prev,
                productId,
                productType,
                registrationFee: Number(registrationFee || 0),
                registrationFeeType: feeType,
                requiresVerification: needsVerification,
                sumsubVerificationCompleted: !needsVerification ? true : prev.sumsubVerificationCompleted
              }));
              setPaymentCompleted(feeType === 'NO_FEE');
              setPaymentStatus(feeType === 'NO_FEE' ? 'COMPLETED' : 'PENDING');
            }}
            selectedProductId={formData.productId}
          />
        );
      case 1:
        return (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '12px', 
            width: '100%',
            marginBottom: '8px',
            maxHeight: 'calc(100vh - 250px)',
            overflowY: 'auto',
            padding: '8px'
          }}>
            {input('Company Name', 'companyName')}
            {input('Registration Number', 'registrationNumber')}
            {input('Date of Incorporation', 'dateOfIncorporation', 'date')}
            {input('Country of Incorporation', 'countryOfIncorporation')}
            <div className="form-group">
              <label className="form-label">Business Type</label>
              <select
                className="form-input"
                value={formData.businessType}
                onChange={(e) => handleChange('businessType', e.target.value)}
                style={{ height: '40px', fontSize: '14px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#ffffff' }}
              >
                <option value="">Select Business Type</option>
                <option value="Private Limited Company">Private Limited Company</option>
                <option value="Partnership">Partnership</option>
                <option value="Trust">Trust</option>
                <option value="City Council/Local Authorities">City Council/Local Authorities</option>
                <option value="Unlimited Liability">Unlimited Liability</option>
                <option value="Provident/Pension Fund">Provident/Pension Fund</option>
                <option value="Investment Firm">Investment Firm</option>
                <option value="Semi-Government Organization">Semi-Government Organization</option>
                <option value="Charity/Club/Foundation">Charity/Club/Foundation</option>
                <option value="UCITS/AIF/Fund">UCITS/AIF/Fund</option>
                <option value="Trade Union">Trade Union</option>
                <option value="Government Organization">Government Organization</option>
                <option value="Other">Other (specify)</option>
              </select>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                Note: All statutory corporate documents of the Company should be attached.
              </div>
            </div>
            {formData.businessType === 'Other' && (
              <div className="form-group">
                <label className="form-label">Please specify</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.businessTypeOther || ''}
                  onChange={(e) => handleChange('businessTypeOther', e.target.value)}
                  placeholder="Specify business type"
                />
              </div>
            )}
            {input('Industry', 'industry')}
            {input('Website', 'website')}
            {input('Email', 'email', 'email')}
            <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '-8px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={sendVerificationLink} disabled={emailVerificationLoading || !formData.email} className="btn-secondary">
                  {emailVerificationLoading ? 'Sending...' : 'Send verification link'}
                </button>
                {emailVerificationSent && (
                  <button type="button" onClick={refreshEmailVerificationStatus} disabled={emailVerificationLoading} className="btn-secondary">
                    I verified, check status
                  </button>
                )}
              </div>
              {(emailVerificationMessage || emailVerified) && (
                <p style={{ marginTop: '8px', marginBottom: 0, color: emailVerified ? '#16a34a' : '#6b7280', fontSize: '13px' }}>
                  {emailVerified ? 'Email verified. You can continue.' : emailVerificationMessage}
                </p>
              )}
            </div>
            {input('Phone', 'phone', 'tel')}
          </div>
        );
      case 1:
        return (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '24px', 
            width: '100%',
            marginBottom: '8px',
            maxHeight: 'calc(100vh - 250px)',
            overflowY: 'auto',
            padding: '8px'
          }}>
            {input('Company Name', 'companyName')}
            {input('Registration Number', 'registrationNumber')}
            {input('Date of Incorporation', 'dateOfIncorporation', 'date')}
            {input('Country of Incorporation', 'countryOfIncorporation')}
            <div className="form-group">
              <label className="form-label">Business Type</label>
              <select
                className="form-input"
                value={formData.businessType}
                onChange={(e) => handleChange('businessType', e.target.value)}
                style={{ height: '40px', fontSize: '14px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#ffffff' }}
              >
                <option value="">Select Business Type</option>
                <option value="Private Limited Company">Private Limited Company</option>
                <option value="Public Limited Company">Public Limited Company</option>
                <option value="Partnership">Partnership</option>
                <option value="Sole Proprietorship">Sole Proprietorship</option>
                <option value="Limited Liability Partnership">Limited Liability Partnership</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {formData.businessType === 'Other' && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Please specify</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.businessTypeOther}
                  onChange={(e) => handleChange('businessTypeOther', e.target.value)}
                  placeholder="Specify business type"
                />
              </div>
            )}
            {input('Industry', 'industry')}
            {input('Website', 'website')}
            {input('Email', 'email', 'email')}
            <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '-8px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" onClick={sendVerificationLink} disabled={emailVerificationLoading || !formData.email} className="btn-secondary">
                  {emailVerificationLoading ? 'Sending...' : 'Send verification link'}
                </button>
                {emailVerificationSent && (
                  <button type="button" onClick={refreshEmailVerificationStatus} disabled={emailVerificationLoading} className="btn-secondary">
                    I verified, check status
                  </button>
                )}
              </div>
              {(emailVerificationMessage || emailVerified) && (
                <p style={{ marginTop: '8px', marginBottom: 0, color: emailVerified ? '#16a34a' : '#6b7280', fontSize: '13px' }}>
                  {emailVerified ? 'Email verified. You can continue.' : emailVerificationMessage}
                </p>
              )}
            </div>
            {input('Phone', 'phone')}
          </div>
        );
      case 2:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
            {addressInput('Street Address', 'street', 'registeredAddress')}
            {addressInput('City', 'city', 'registeredAddress')}
            {addressInput('State/Province', 'state', 'registeredAddress')}
            {addressInput('Postal Code', 'postalCode', 'registeredAddress')}
            {addressInput('Country', 'country', 'registeredAddress')}
          </div>
        );
      case 3:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
            {addressInput('Street Address', 'street', 'businessAddress')}
            {addressInput('City', 'city', 'businessAddress')}
            {addressInput('State/Province', 'state', 'businessAddress')}
            {addressInput('Postal Code', 'postalCode', 'businessAddress')}
            {addressInput('Country', 'country', 'businessAddress')}
          </div>
        );
      case 4:
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Directors & Shareholders</h3>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    directors: [...prev.directors, { name: '', position: '', nationality: '', shareholding: '', address: '' }]
                  }));
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                + Add Director/Shareholder
              </button>
            </div>
            {formData.directors.map((director, index) => (
              <div key={index} style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4>Signatory {index + 1}</h4>
                  {formData.signatories.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          signatories: prev.signatories.filter((_, i) => i !== index)
                        }));
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={director.name}
                      onChange={(e) => handleArrayChange('directors', index, 'name', e.target.value)}
                      placeholder="Enter director name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Position</label>
                    <input
                      type="text"
                      className="form-input"
                      value={director.position}
                      onChange={(e) => handleArrayChange('directors', index, 'position', e.target.value)}
                      placeholder="Enter position"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nationality</label>
                    <input
                      type="text"
                      className="form-input"
                      value={director.nationality}
                      onChange={(e) => handleArrayChange('directors', index, 'nationality', e.target.value)}
                      placeholder="Enter nationality"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Shareholding %</label>
                    <input
                      type="text"
                      className="form-input"
                      value={director.shareholding}
                      onChange={(e) => handleArrayChange('directors', index, 'shareholding', e.target.value)}
                      placeholder="Enter shareholding percentage"
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={director.address}
                      onChange={(e) => handleArrayChange('directors', index, 'address', e.target.value)}
                      placeholder="Enter full address"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 5:
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Authorized Signatories</h3>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    signatories: [...prev.signatories, { name: '', position: '', authority: '', address: '' }]
                  }));
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                + Add Signatory
              </button>
            </div>
            {formData.signatories.map((signatory, index) => (
              <div key={index} style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4>Signatory {index + 1}</h4>
                  {formData.signatories.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          signatories: prev.signatories.filter((_, i) => i !== index)
                        }));
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={signatory.name}
                      onChange={(e) => handleArrayChange('signatories', index, 'name', e.target.value)}
                      placeholder="Enter signatory name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Position</label>
                    <input
                      type="text"
                      className="form-input"
                      value={signatory.position}
                      onChange={(e) => handleArrayChange('signatories', index, 'position', e.target.value)}
                      placeholder="Enter position"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Authority</label>
                    <input
                      type="text"
                      className="form-input"
                      value={signatory.authority}
                      onChange={(e) => handleArrayChange('signatories', index, 'authority', e.target.value)}
                      placeholder="Enter signing authority"
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={signatory.address}
                      onChange={(e) => handleArrayChange('signatories', index, 'address', e.target.value)}
                      placeholder="Enter full address"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 6:
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', width: '100%' }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Business Activity Description</label>
              <textarea
                className="form-input"
                value={formData.businessActivity}
                onChange={(e) => handleChange('businessActivity', e.target.value)}
                placeholder="Describe your business activities"
                rows={4}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Expected Monthly Turnover</label>
              <input
                type="text"
                className="form-input"
                value={formData.expectedMonthlyTurnover}
                onChange={(e) => handleChange('expectedMonthlyTurnover', e.target.value)}
                placeholder="e.g., £10,000"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Source of Funds</label>
              <input
                type="text"
                className="form-input"
                value={formData.sourceOfFunds}
                onChange={(e) => handleChange('sourceOfFunds', e.target.value)}
                placeholder="e.g., Business operations"
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Purpose of Account</label>
              <textarea
                className="form-input"
                value={formData.purposeOfAccount}
                onChange={(e) => handleChange('purposeOfAccount', e.target.value)}
                placeholder="Describe the purpose of this account"
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        );
      case 7:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px' }}>
              <h3 style={{ margin: 0, marginBottom: 12, color: '#1f2937' }}>Compliance</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="checkbox"
                    checked={formData.underSanctions}
                    onChange={(e) => handleChange('underSanctions', e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <label style={{ fontSize: '15px', color: '#1f2937' }}>Under Sanctions</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="checkbox"
                    checked={formData.pep}
                    onChange={(e) => handleChange('pep', e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <label style={{ fontSize: '15px', color: '#1f2937' }}>Politically Exposed Person</label>
                </div>
              </div>
            </div>
          </div>
        );
      case 8:
        return (
          <div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Document Requirements</h3>
              <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 12px 0' }}>Please select the documents you will provide:</p>
              </div>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Document Selection</h3>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ padding: '12px', border: '1px solid #d1d5db', textAlign: 'left' }}>Document</th>
                      <th style={{ padding: '12px', border: '1px solid #d1d5db', textAlign: 'left' }}>Select</th>
                      <th style={{ padding: '12px', border: '1px solid #d1d5db', textAlign: 'left' }}>Comments</th>
                      <th style={{ padding: '12px', border: '1px solid #d1d5db', textAlign: 'left' }}>Upload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries({
                      certificateOfIncorporation: 'Certificate of Incorporation',
                      memorandumOfAssociation: 'Memorandum of Association',
                      articlesOfAssociation: 'Articles of Association',
                      boardResolution: 'Board Resolution',
                      beneficialOwnership: 'Beneficial Ownership Register',
                      financialStatements: 'Financial Statements',
                      businessPlan: 'Business Plan',
                      other: 'Other'
                    }).map(([key, label]) => (
                      <tr key={key}>
                        <td style={{ padding: '12px', border: '1px solid #d1d5db' }}>{label}</td>
                        <td style={{ padding: '12px', border: '1px solid #d1d5db' }}>
                          <input
                            type="checkbox"
                            checked={formData.documents[key as keyof typeof formData.documents] as boolean}
                            onChange={(e) => handleChange(`documents.${key}`, e.target.checked)}
                            style={{ width: '20px', height: '20px' }}
                          />
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #d1d5db' }}>
                          <input
                            type="text"
                            value={formData.documentComments[key as keyof typeof formData.documentComments] as string || ''}
                            onChange={(e) => handleChange(`documentComments.${key}`, e.target.value)}
                            placeholder="Comments"
                            style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '12px', border: '1px solid #d1d5db' }}>
                          <input
                            type="file"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              handleChange(`uploadedDocuments.${key}`, files);
                              handleChange(`documents.${key}`, files.length > 0);
                            }}
                            style={{ width: '100%' }}
                          />
                          {formData.uploadedDocuments[key]?.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                              {formData.uploadedDocuments[key].length} file(s) uploaded
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {verificationRequired && (
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>SUMSUB ID Verification</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  checked={formData.sumsubVerificationCompleted}
                  onChange={(e) => handleChange('sumsubVerificationCompleted', e.target.checked)}
                  style={{ width: '20px', height: '20px' }}
                />
                <label style={{ fontSize: '15px', color: '#1f2937' }}>SUMSUB verification completed</label>
              </div>
              <button
                type="button"
                onClick={startKYCProcess}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Start SUMSUB Verification
              </button>
            </div>
            )}
          </div>
        );
      case 9:
        return (
          <div>
            <div ref={reviewRef} style={{ padding: '24px', background: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
              <h3 style={{ marginTop: 0, marginBottom: 24 }}>Review All Details</h3>
              
              {/* Product Information */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>1. Selected Product</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Product ID:</strong> {formData.productId}</div>
                  <div><strong>Product Type:</strong> {formData.productType}</div>
                </div>
              </div>
              
              {/* Company Information */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>2. Company Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Company Name:</strong> {formData.companyName}</div>
                  <div><strong>Registration Number:</strong> {formData.registrationNumber}</div>
                  <div><strong>Date of Incorporation:</strong> {formData.dateOfIncorporation}</div>
                  <div><strong>Country of Incorporation:</strong> {formData.countryOfIncorporation}</div>
                  <div><strong>Business Type:</strong> {formData.businessType}{formData.businessType === 'Other' && formData.businessTypeOther ? ` - ${formData.businessTypeOther}` : ''}</div>
                  <div><strong>Industry:</strong> {formData.industry}</div>
                  <div><strong>Website:</strong> {formData.website}</div>
                  <div><strong>Email:</strong> {formData.email}</div>
                  <div><strong>Phone:</strong> {formData.phone}</div>
                </div>
              </div>

              {/* Registered Address */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>3. Registered Address</h4>
                <div>
                  {formData.registeredAddress.street}, {formData.registeredAddress.city}, {formData.registeredAddress.state} {formData.registeredAddress.postalCode}, {formData.registeredAddress.country}
                </div>
              </div>

              {/* Business Address */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>4. Business Address</h4>
                <div>
                  {formData.businessAddress.street}, {formData.businessAddress.city}, {formData.businessAddress.state} {formData.businessAddress.postalCode}, {formData.businessAddress.country}
                </div>
              </div>

              {/* Directors & Shareholders */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>5. Directors & Shareholders</h4>
                {formData.directors.map((director, index) => (
                  <div key={index} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>Director/Shareholder {index + 1}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                      <div><strong>Name:</strong> {director.name}</div>
                      <div><strong>Position:</strong> {director.position}</div>
                      <div><strong>Nationality:</strong> {director.nationality}</div>
                      <div><strong>Shareholding:</strong> {director.shareholding}</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong>Address:</strong> {director.address}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Authorized Signatories */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>6. Authorized Signatories</h4>
                {formData.signatories.map((signatory, index) => (
                  <div key={index} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>Signatory {index + 1}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                      <div><strong>Name:</strong> {signatory.name}</div>
                      <div><strong>Position:</strong> {signatory.position}</div>
                      <div><strong>Authority:</strong> {signatory.authority}</div>
                      <div style={{ gridColumn: '1 / -1' }}><strong>Address:</strong> {signatory.address}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Business Activity */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>7. Business Activity</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ gridColumn: '1 / -1' }}><strong>Business Activity Description:</strong> {formData.businessActivity}</div>
                  <div><strong>Expected Monthly Turnover:</strong> {formData.expectedMonthlyTurnover}</div>
                  <div><strong>Source of Funds:</strong> {formData.sourceOfFunds}</div>
                  <div style={{ gridColumn: '1 / -1' }}><strong>Purpose of Account:</strong> {formData.purposeOfAccount}</div>
                </div>
              </div>

              {/* Compliance */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>8. Compliance</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Under Sanctions:</strong> {formData.underSanctions ? 'Yes' : 'No'}</div>
                  <div><strong>Politically Exposed Person:</strong> {formData.pep ? 'Yes' : 'No'}</div>
                </div>
              </div>

              {/* Documents */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>9. Documents</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Certificate of Incorporation:</strong> {formData.documents.certificateOfIncorporation ? 'Yes' : 'No'}</div>
                  <div><strong>Memorandum of Association:</strong> {formData.documents.memorandumOfAssociation ? 'Yes' : 'No'}</div>
                  <div><strong>Articles of Association:</strong> {formData.documents.articlesOfAssociation ? 'Yes' : 'No'}</div>
                  <div><strong>Board Resolution:</strong> {formData.documents.boardResolution ? 'Yes' : 'No'}</div>
                  <div><strong>Beneficial Ownership:</strong> {formData.documents.beneficialOwnership ? 'Yes' : 'No'}</div>
                  <div><strong>Financial Statements:</strong> {formData.documents.financialStatements ? 'Yes' : 'No'}</div>
                  <div><strong>Business Plan:</strong> {formData.documents.businessPlan ? 'Yes' : 'No'}</div>
                  <div><strong>Other:</strong> {formData.documents.other ? `Yes - ${formData.documents.otherSpecify}` : 'No'}</div>
                </div>
                {verificationRequired && (
                <div style={{ marginTop: '12px' }}>
                  <strong>SUMSUB Verification:</strong> {formData.sumsubVerificationCompleted ? 'Completed' : 'Not Completed'}
                </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: '24px', padding: '24px', background: '#f8fafc', borderRadius: '12px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Digital Signature</h3>
              <div style={{ marginBottom: '16px' }}>
                <SignaturePad
                  onChange={(dataUrl: string | null) => handleChange('signatureDataUrl', dataUrl)}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0' }}>
                <input type="checkbox" style={{ width: '20px', height: '20px', marginTop: '2px' }} checked={formData.terms} onChange={(e) => handleChange('terms', e.target.checked)} />
                <div>
                  <label style={{ fontSize: '15px', color: '#1f2937' }}>I agree to the Terms and Privacy Policy</label>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Required to proceed with registration</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
                <input type="checkbox" style={{ width: '20px', height: '20px', marginTop: '2px' }} checked={formData.marketing} onChange={(e) => handleChange('marketing', e.target.checked)} />
                <div>
                  <label style={{ fontSize: '15px', color: '#1f2937' }}>I consent to receive marketing communications</label>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Optional - you can unsubscribe at any time</div>
                </div>
              </div>

              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, marginBottom: 8, color: '#1f2937' }}>Registration Payment</h4>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  {formData.registrationFeeType === 'NO_FEE' && 'No registration payment is required for your selected tier.'}
                  {formData.registrationFeeType === 'FIXED_ONE_OFF' && <>A one-time registration fee of <strong>EUR {Number(formData.registrationFee || 0).toFixed(2)}</strong> for your selected tier is required before account creation.</>}
                  {formData.registrationFeeType === 'RECURRING_MONTHLY' && <>A recurring monthly fee of <strong>EUR {Number(formData.registrationFee || 0).toFixed(2)}</strong> for your selected tier is required before account creation.</>}
                </p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {formData.registrationFeeType !== 'NO_FEE' && (
                    <button
                      type="button"
                      onClick={handleRegistrationPayment}
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <FaCcStripe />
                        {formData.registrationFeeType === 'RECURRING_MONTHLY' ? 'Start Monthly Subscription' : 'Pay Registration Fee'}
                      </span>
                    </button>
                  )}
                  <span style={{ color: paymentCompleted ? '#16a34a' : '#475569' }}>
                    Status: <strong>{paymentStatus}</strong>
                  </span>
                </div>
                {paymentSessionId && <p style={{ marginBottom: 0, fontSize: 12, color: '#64748b' }}>Session: {paymentSessionId}</p>}
              </div>
            </div>
          </div>
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  // KYC handler functions
  const startKYCProcess = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Prepare KYC data for business registration
      // For business registration, we'll use the company information
      const kycData = {
        firstName: formData.directors[0]?.name?.split(' ')[0] || formData.companyName,
        lastName: formData.directors[0]?.name?.split(' ').slice(1).join(' ') || '',
        email: formData.email,
        phoneNumber: formData.phone,
        dateOfBirth: formData.dateOfIncorporation || '1990-01-01', // Use incorporation date or default
        address: {
          street: formData.registeredAddress.street,
          city: formData.registeredAddress.city,
          state: formData.registeredAddress.state,
          postalCode: formData.registeredAddress.postalCode,
          country: formData.registeredAddress.country
        }
      };

      // Use company email as username for KYC
      const kycResponse = await kycService.createApplicant(kycData, formData.email);
      console.log('KYC Response:', kycResponse);

      if (kycResponse.success && kycResponse.data) {
        console.log('Setting KYC state:', {
          applicantId: kycResponse.data.applicantId,
          accessToken: kycResponse.data.accessToken ? '***' + kycResponse.data.accessToken.slice(-8) : 'null'
        });
        setKycApplicantId(kycResponse.data.applicantId);
        setKycAccessToken(kycResponse.data.accessToken);
        setShowKycModal(true);
      } else {
        setError('Failed to create KYC applicant');
      }
    } catch (error: any) {
      console.error('KYC creation error:', error);
      setError('Error creating KYC applicant: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKYCComplete = (status: string) => {
    console.log('KYC completed with status:', status);
    setKycStatus(status as 'pending' | 'completed' | 'rejected');
    setShowKycModal(false);
  };

  const handleKYCError = (error: string) => {
    console.error('KYC error:', error);
    setError(error);
    setShowKycModal(false);
  };

  const handleKYCClose = () => {
    setShowKycModal(false);
  };

  // Initialize CSS variables from branding
  const initializeBrandingStyles = () => {
    const root = document.documentElement;
    
    // Core colors
    root.style.setProperty('--primary-color', branding.primaryColor);
    root.style.setProperty('--text-color', branding.textColor);
    root.style.setProperty('--text-primary', branding.textColor);
    root.style.setProperty('--background', branding.backgroundColor);
    root.style.setProperty('--text-secondary', '#6b7280');
    root.style.setProperty('--border', '#e2e8f0');
    root.style.setProperty('--success', '#059669');
    root.style.setProperty('--error', '#dc2626');
    
    // Sidebar
    root.style.setProperty('--sidebar-width', branding.sidebar.width);
    root.style.setProperty('--sidebar-background', branding.sidebar.background);
    root.style.setProperty('--sidebar-padding', branding.sidebar.padding);
    root.style.setProperty('--sidebar-text-color', branding.sidebar.textColor);
    
    // Registration Main
    root.style.setProperty('--registration-main-padding', branding.registration.main.padding);
    root.style.setProperty('--registration-main-background', branding.registration.main.background);
  };

  return (
    <div className="registration-container">
      <div className="registration-sidebar">
        <div style={{ marginBottom: '24px' }}>
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 600, 
            color: 'white', 
            marginBottom: '4px' 
          }}>
            {branding.sidebar.logoText}
          </div>
        </div>
        
        <div style={{ flex: 1 }}>
          <h3 style={{ 
            fontSize: '24px', 
            fontWeight: 600, 
            color: 'white', 
            marginBottom: '12px',
            lineHeight: '1.2'
          }}>
            Enhanced Business Registration
          </h3>
          <div style={{ 
            fontSize: '14px', 
            color: '#94a3b8', 
            lineHeight: '1.5' 
          }}>
            A comprehensive business registration flow with digital signature and PDF download.
          </div>
        </div>
        
        <div style={{ 
          fontSize: '12px', 
          color: '#64748b'
        }}>
          © {new Date().getFullYear()} {branding.sidebar.logoText}. All rights reserved.
        </div>
      </div>
      <div className="registration-main">
        {/* Classic Horizontal Stepper - All Steps Visible */}
        <div className="stepper-container">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            padding: '0 10px'
          }}>
            {steps.map((label, index) => (
              <div key={label} style={{ 
                display: 'flex', 
                alignItems: 'center',
                flexShrink: 0,
                minWidth: '80px'
              }}>
                {/* Step Circle */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  minWidth: '80px'
                }}>
                  <div style={{ 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    backgroundColor: index === activeStep ? '#3b82f6' : index < activeStep ? '#10b981' : '#e5e7eb',
                    color: index === activeStep ? 'white' : index < activeStep ? 'white' : '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: index === activeStep ? '2px solid #3b82f6' : index < activeStep ? '2px solid #10b981' : '2px solid #e5e7eb',
                    transition: 'all 0.3s ease'
                  }}>
                    {index < activeStep ? '✓' : index + 1}
                  </div>
                  <div style={{ 
                    marginTop: '6px',
                    fontSize: '10px',
                    color: index === activeStep ? '#3b82f6' : index < activeStep ? '#10b981' : '#6b7280',
                    fontWeight: index === activeStep ? '600' : '500',
                    textAlign: 'center',
                    maxWidth: '80px',
                    lineHeight: '1.1',
                    wordWrap: 'break-word'
                  }}>
                    {label}
                  </div>
                </div>
                
                {/* Connecting Line */}
                {index < steps.length - 1 && (
                  <div style={{ 
                    width: '40px', 
                    height: '2px', 
                    backgroundColor: index < activeStep ? '#10b981' : '#e5e7eb',
                    margin: '0 5px',
                    transition: 'all 0.3s ease'
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>

      <div className="form-container">
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', margin: 0 }}>
                {steps[activeStep]}
              </h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* TEMPORARILY HIDDEN - Fill Mock Data button 
                <button
                  type="button"
                  onClick={fillWithMockData}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#3b82f6',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Fill Mock Data
                </button>
                */}
                <button
                  type="button"
                  onClick={clearAllData}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    border: '1px solid #dc2626',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
              Step {activeStep + 1} of {steps.length}
            </p>
          </div>

          <div className="form-content">
            {renderStep()}
          </div>

          <div className="button-group">
            <button
              type="button"
              onClick={handleBack}
              disabled={activeStep === 0}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: activeStep === 0 ? 0.5 : 1
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!isStepValid || loading}
              style={{
                padding: '12px 24px',
                backgroundColor: isStepValid && !loading ? '#3b82f6' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isStepValid && !loading ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {loading ? 'Generating PDF...' : activeStep === steps.length - 1 ? 'Complete Registration' : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {showKycModal && kycApplicantId && kycAccessToken && (
        <SumsubKYC
          applicantId={kycApplicantId}
          accessToken={kycAccessToken}
          onComplete={handleKYCComplete}
          onError={handleKYCError}
          onClose={handleKYCClose}
        />
      )}
    </div>
  );
};

export default EnhancedBusinessRegister;
